import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { Button, Dropdown } from "@mapmycustomers/ui";

import { updateLastUsedLayout } from "@app/store/iam/actions";
import { getLayoutsForEntity } from "@app/store/layout";
import { RootState } from "@app/store/rootReducer";

import styles from "./FormLayoutSelector.module.scss";

interface FormLayoutSelectorProps {
  disabled?: boolean;
  entityType: EntityTypesSupportingFieldCustomization;
  layout?: FormLayout;
  onChange?: (layout: FormLayout) => void;
  scrollable?: boolean;
}

const FormLayoutSelector = ({
  disabled,
  entityType,
  layout,
  layouts,
  onChange,
  onUpdateLastUsedLayout,
  scrollable = true,
}: FormLayoutSelectorProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const [visible, setVisible] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const handleMenuClick = useCallback(() => {
    setVisible(false);
    setTooltipVisible(false);
  }, [setTooltipVisible, setVisible]);

  const handleLayoutChange = useCallback(
    (layout: FormLayout) => {
      setVisible(false);
      setTooltipVisible(false);

      onChange?.(layout);
      onUpdateLastUsedLayout({ entityType, layoutId: layout.id });
    },
    [entityType, onChange, onUpdateLastUsedLayout, setTooltipVisible, setVisible]
  );

  const handleDropdownVisibleChange = useCallback(
    (value: boolean) => {
      setVisible(value);
      if (!value) {
        setTooltipVisible(false);
      }
    },
    [setTooltipVisible, setVisible]
  );

  const handleTooltipVisibleChange = useCallback(
    (value: boolean) => setTooltipVisible(value),
    [setTooltipVisible]
  );

  if (layouts.length < 2) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Dropdown
        disabled={disabled}
        menu={{
          className: cn(styles.menu, { [styles.scrollable]: scrollable }),
          items: layouts.map((layout) => ({
            key: layout.id,
            label: layout.name,
            onClick: () => handleLayoutChange(layout),
          })),
          onClick: handleMenuClick,
          selectedKeys: layout ? [String(layout.id)] : [],
        }}
        onOpenChange={handleDropdownVisibleChange}
        open={visible}
        trigger={disabled ? [] : ["click"]}
      >
        <Tooltip
          destroyTooltipOnHide
          onOpenChange={handleTooltipVisibleChange}
          open={!disabled && tooltipVisible && !visible}
          overlayClassName={styles.tooltip}
          placement="top"
          title={intl.formatMessage({
            id: "formLayoutSelector.tooltip",
            defaultMessage: "Switch Layout",
            description: "Switch Layout",
          })}
          trigger={disabled || visible ? [] : ["hover"]}
        >
          <Button
            className={styles.button}
            disabled={disabled}
            rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
            type="text"
          >
            <span className={styles.label}>
              {layout?.name ??
                intl.formatMessage({
                  id: "formLayoutSelector.select",
                  defaultMessage: "(Select layout)",
                  description: "Select Layout",
                })}
            </span>
          </Button>
        </Tooltip>
      </Dropdown>
    </div>
  );
};

const mapStateToProps = (state: RootState, { entityType }: FormLayoutSelectorProps) => ({
  layouts: getLayoutsForEntity(state, entityType),
});

const mapDispatchToProps = {
  onUpdateLastUsedLayout: updateLastUsedLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormLayoutSelector);
